/**
 * 사내결재 문서함 — [지출결재 문서] 탭 (v2.50.0 신설 · v2.55.0 메뉴 개명).
 *
 *   상단: [새 구입지급결의서] [새 개인경비정산서] (법인카드는 [법인카드 사용내역] 탭에서).
 *   ① 내 작성중·반려 문서 — 이어서 작성 (본인만 보임).
 *   ② 완결(최종승인) 문서 목록 — 종류·연도·계정과목·작성자·검색 필터 + 합계.
 *      행 클릭 = 문서 모달, 📄 = 확정 PDF 바로 미리보기.
 *
 * 가시성은 서버가 행 단위로 거른다: 작성자 본인 + 결재선 당사자 + admin.
 * 진행 중 결재(검토대기~경영지원승인)는 [내 결재함]에서.
 */

import { Fragment, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as api from '../../lib/expense-api';
import { ExpenseDocModal, EXPENSE_STATUS_BADGE } from '../../components/ExpenseDocModal';
import {
  ExpenseRemitModal, RemitStateLabel, payDueOf, type RemitTarget,
} from '../../components/ExpenseRemitModal';
import { DateInput } from '../../components/DateInput';
import { todayKst } from '../../lib/date-label';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { useAuth } from '../../lib/auth-context';
import { LoadingLines } from '../../components/ui/Skeleton';

const fmtKrw = (n: number): string => Math.round(n).toLocaleString('ko-KR');

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

/**
 * 이 탭이 다루는 문서 종류 (v2.134.0 · 2026-09-08 사용자 확정).
 *
 * **법인카드사용내역서는 여기서 뺀다** — 카드 결제건은 [법인카드 사용내역] 탭에 그대로 있고
 * 금액 통계는 [지출 현황]에서 내고 있어서다(운영 실측: 완결 45건 중 40건이 카드라
 * 나머지 문서가 묻혀 있었다).
 *
 * ⭐**더하기(허용 목록)로 적는다** — 「법인카드만 빼고 전부」로 적으면 문서 종류를 늘릴 때마다
 *   새 종류가 이 목록에 조용히 섞여 든다(v2.113.0 에 실제로 겪은 사고).
 * ⛔공용 `api.EXPENSE_DOC_TYPES` 를 줄이지 말 것 — 결재함의 금액 칸·문서 모달의 종류 판정·
 *   지출대장 엑셀이 그것을 함께 쓴다(타입체크·빌드는 통과한 채로 깨진다).
 */
const TAB_DOC_TYPES: api.ExpenseDocType[] = ['구입지급결의서', '개인경비정산서'];

/** 지출 분류 배지 색 (v2.134.0) — 굵기·색으로만 구분하고 글자 크기로 위계를 주지 않는다 */
const SPEND_BADGE: Record<string, string> = {
  '비품 및 소모품': 'bg-slate-100 text-slate-700',
  마케팅: 'bg-amber-100 text-amber-800',
  판매물품: 'bg-sky-100 text-sky-800',
  수출입관련: 'bg-violet-100 text-violet-800',
  기타: 'bg-slate-100 text-slate-600',
  '(미지정)': 'bg-rose-100 text-rose-700',
};

export function ExpenseDocsTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<api.ExpenseDocType | ''>('');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [account, setAccount] = useState('');
  const [author, setAuthor] = useState('');
  const [q, setQ] = useState('');
  /**
   * 💸 송금 거르기 (v2.267.0 · 개발 계획 #19) — '' 전체 · pending 송금 대기만 · done 송금완료만.
   * ⭐송금 대기는 **연도와 상관없이** 다 보여 준다 — 12월 문서를 1월에 보내는 일이 있고,
   *   위의 「송금 대기 N건」 숫자도 연도를 안 가린다(둘이 어긋나면 「숫자는 있는데 목록이 비었다」가 된다).
   */
  const [remitFilter, setRemitFilter] = useState<'' | 'pending' | 'done'>('');
  const [openDoc, setOpenDoc] = useState<{ id: number | null; type?: api.ExpenseDocType } | null>(null);
  // 💸 송금완료 창 · 저장 뒤 알림 한 줄 (v2.267.0)
  const [remitTarget, setRemitTarget] = useState<RemitTarget | null>(null);
  const [remitNotice, setRemitNotice] = useState<string | null>(null);

  // 완결 목록 (필터 서버 적용)
  const finalQuery = useQuery({
    queryKey: ['expense', 'docs', 'final', typeFilter, year, account, author, q, remitFilter],
    queryFn: () => api.listExpenseDocs({
      final: true,
      types: TAB_DOC_TYPES,
      type: typeFilter || undefined,
      year: remitFilter === 'pending' ? undefined : year,
      account: account || undefined,
      author: author || undefined,
      q: q.trim() || undefined,
      remit: remitFilter || undefined,
    }),
    staleTime: 15_000,
  });

  // 내 작성중·반려 (이어서 작성) — 필터 무관하게 항상
  const draftQuery = useQuery({
    queryKey: ['expense', 'docs', 'mydrafts'],
    queryFn: () => api.listExpenseDocs({ notFinal: true, types: TAB_DOC_TYPES }),
    staleTime: 15_000,
  });
  const myDrafts = (draftQuery.data?.rows ?? []).filter(
    (r) => r.authorId === user?.userId && (r.status === '작성중' || r.status === '반려'),
  );

  const users = useQuery({ queryKey: ['expense', 'users'], queryFn: api.listPortalUsers, staleTime: 300_000 });
  const isApprover = finalQuery.data?.isApprover ?? false;
  /** 💸 송금완료를 표시할 수 있나 — 경영지원 지정 계정·admin (서버가 정한다 · v2.267.0) */
  const canMarkRemit = finalQuery.data?.canMarkRemit === true;
  const remitPendingCount = finalQuery.data?.remitPendingCount ?? 0;

  const rows = finalQuery.data?.rows ?? [];
  const truncated = finalQuery.data?.truncated ?? false;
  const totals = useMemo(() => ({
    count: rows.length,
    amount: rows.reduce((s, r) => s + r.totalAmount, 0),
  }), [rows]);

  /**
   * 📊 이 목록 요약 (v2.134.0) — **화면이 이미 받아 놓은 목록**을 센다.
   *
   * ⭐새 집계 API 를 만들지 않는다. 요약과 표가 같은 자료에서 나오므로 필터를 어떻게 바꿔도
   *   두 숫자가 구조적으로 어긋날 수 없다(500건 상한에 걸리면 그 경고 배너가 요약에도 적용된다).
   * ⛔[지출 현황] 통계(/stats)는 건드리지 않는다 — 그쪽은 법인카드+개인경비만 세기로
   *   2026-08-23 에 확정했고, 축(사용일)도 다르다. 여기 숫자와 안 맞는 것이 정상이다.
   * ⚠지출 분류는 **구입지급결의서에만 있는 칸**이라 별도 줄로 둔다. 개인경비를 「미지정」으로
   *   같은 표에 세우면 그날로 「합계가 안 맞는다」가 된다.
   */
  const summary = useMemo(() => {
    const byType = new Map<string, { count: number; amount: number }>();
    const byCategory = new Map<string, { count: number; amount: number }>();
    let purchaseCount = 0;
    let purchaseAmount = 0;
    for (const r of rows) {
      const t = byType.get(r.docType) ?? { count: 0, amount: 0 };
      t.count += 1; t.amount += r.totalAmount;
      byType.set(r.docType, t);
      if (r.docType === '구입지급결의서') {
        purchaseCount += 1;
        purchaseAmount += r.totalAmount;
        const key = r.spendCategory ?? '(미지정)';
        const c = byCategory.get(key) ?? { count: 0, amount: 0 };
        c.count += 1; c.amount += r.totalAmount;
        byCategory.set(key, c);
      }
    }
    // 정해 둔 차례대로 — 목록에 없는 분류는 줄을 만들지 않는다. (미지정)은 맨 뒤.
    const order = [...api.SPEND_CATEGORIES, '(미지정)'];
    const categories = order
      .filter((k) => byCategory.has(k))
      .map((k) => ({ key: k, ...byCategory.get(k)! }));
    return {
      byType: TAB_DOC_TYPES.map((t) => ({ type: t, ...(byType.get(t) ?? { count: 0, amount: 0 }) })),
      categories,
      purchaseCount,
      purchaseAmount,
    };
  }, [rows]);

  /**
   * 작성자별 묶음 (v2.62.0) — 2026-08-07 사용자 요청:
   * "사용자 이름만 나오고 최근 업로드 파일만 보이게, 열면 그 사람 문서가 쭈욱".
   *
   * ⚠묶음은 **서버가 이미 걸러 준 rows 안에서만** 만든다.
   *   직원 명부(/expense/users)로 만들면 **내가 못 보는 사람이 「0건」 줄로 뜬다** — 가시성 경계가 새는 것.
   */
  const groups = useMemo(() => {
    const byAuthor = new Map<string, { authorId: string; name: string; dept: string | null; docs: typeof rows }>();
    for (const r of rows) {
      const g = byAuthor.get(r.authorId)
        ?? { authorId: r.authorId, name: r.authorName, dept: r.authorDept, docs: [] };
      g.docs.push(r);
      byAuthor.set(r.authorId, g);
    }
    return [...byAuthor.values()]
      .map((g) => {
        // 최근 = 귀속월이 가장 늦은 것 (같으면 문서번호가 큰 것)
        const docs = [...g.docs].sort((a, b) =>
          b.attributedFrom.localeCompare(a.attributedFrom) || b.docNo.localeCompare(a.docNo));
        return {
          ...g,
          docs,
          latest: docs[0]!,
          count: docs.length,
          amount: docs.reduce((s, d) => s + d.totalAmount, 0),
          /** 💸 이 사람 문서 중 송금 대기 (v2.267.0) — 접힌 줄에도 보이게 · 상태는 서버 remitState (v2.268.0) */
          remitPending: docs.filter((d) => d.remitState === 'pending').length,
        };
      })
      /**
       * 가장 최근 자료를 낸 사람이 위로 (2026-08-07 사용자 확정).
       * ⚠기준은 **귀속월**이지 업로드 시각이 아니다 — 어제 올렸어도 1월분이면 아래로 간다.
       *   필터·표기를 전부 귀속월 축으로 통일했으므로 정렬만 시각 축으로 두면 축이 갈라진다.
       */
      .sort((a, b) => b.latest.attributedFrom.localeCompare(a.latest.attributedFrom)
        || b.amount - a.amount);
  }, [rows]);

  /**
   * 펼친 작성자 — 기본은 **전부 접힘**(사용자 요청).
   * ⚠키는 authorId **문자열**. 객체를 쓰면 refetch 마다 신원이 바뀌어 펼쳐 둔 줄이 조용히 접힌다.
   */
  const [openAuthors, setOpenAuthors] = useState<Set<string>>(new Set());
  const toggleAuthor = (id: string) => setOpenAuthors((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /**
   * 💸 「송금 대기」는 작성자 묶음이 아니라 **지급일 순 한 줄 목록**으로 (v2.268.0 · 재검토 B) — 경영지원의 일 순서.
   * 지급일이 이른 것(지난 것)이 위 · 지급일 없는 문서는 맨 아래 · 같으면 문서번호 순.
   */
  const flatPending = remitFilter === 'pending';
  const pendingRows = useMemo(() => (!flatPending ? [] : [...rows].sort((a, b) =>
    (a.payDate ?? '9999-99-99').localeCompare(b.payDate ?? '9999-99-99') || a.docNo.localeCompare(b.docNo))),
  [rows, flatPending]);

  /**
   * 💸 여러 건 한 번에 송금완료 (v2.268.0 · 재검토 C) — 송금 대기 목록에서 고르고 송금일(기본 오늘) 하나로.
   * 고른 것 = 문서 id. 목록이 바뀌어 사라진 id 는 셀 때 걸러진다(따로 지우지 않는다).
   */
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [bulkDate, setBulkDate] = useState<string>(() => todayKst());
  const [bulkBusy, setBulkBusy] = useState(false);
  const pickedIds = pendingRows.filter((r) => picked.has(r.id)).map((r) => r.id);
  const togglePick = (id: number) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const runBulk = async () => {
    if (pickedIds.length === 0 || bulkBusy) return;
    const today = todayKst();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(bulkDate)) { setRemitNotice('⚠ 송금일을 넣어 주세요'); return; }
    if (bulkDate > today) { setRemitNotice('⚠ 송금일은 오늘보다 뒤일 수 없습니다'); return; }
    if (!window.confirm(`고른 ${pickedIds.length}건을 송금일 ${bulkDate} 로 송금완료 표시합니다.\n작성자에게 웍스 알림이 갑니다(본인 문서 제외).`)) return;
    setBulkBusy(true);
    try {
      const r = await api.setExpenseRemitBulk(pickedIds, bulkDate);
      setPicked(new Set());
      setRemitNotice(`✓ ${r.done.length}건 송금완료 (송금일 ${bulkDate})`
        + (r.unchanged.length ? ` · 이미 같은 날짜 ${r.unchanged.length}건` : '')
        + (r.skipped.length
          ? ` · ⚠ 못 한 ${r.skipped.length}건: ${r.skipped.map((x) => `${x.docNo ?? `#${x.id}`} ${x.why}`).join(' / ')}`
          : ''));
      void qc.invalidateQueries({ queryKey: ['expense'] });
    } catch (e) {
      setRemitNotice(`⚠ ${(e as Error).message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const chooseRemitFilter = (v: '' | 'pending' | 'done') => {
    setRemitFilter(v);
    setRemitNotice(null);
    setPicked(new Set());
  };
  const openRemit = (r: api.ExpenseDocListRow) => {
    setRemitNotice(null);
    setRemitTarget({ id: r.id, docNo: r.docNo, docType: r.docType, authorName: r.authorName, remitDate: r.remitDate ?? null });
  };

  const selCls = 'ui-input';

  /**
   * 문서 한 줄 — 작성자 묶음 안(flat=false)과 송금 대기 한 줄 목록(flat=true · v2.268.0)이 같은 줄을 쓴다.
   * 칸은 머리글과 그대로 맞는다.
   */
  const docRow = (r: api.ExpenseDocListRow, flat: boolean) => (
    <tr key={r.id}
      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer${flat && picked.has(r.id) ? ' bg-amber-50/60' : ''}`}
      onClick={() => setOpenDoc({ id: r.id })}>
      <td className="py-2 pr-2 font-medium text-slate-700 whitespace-nowrap">
        {!flat && <span className="text-slate-300 mr-1.5">└</span>}
        {r.docNo}
        {r.backfillNote && <BackfillTag note={r.backfillNote} />}
        {/* 한 줄 목록(송금 대기)엔 작성자 줄이 없다 — 문서번호 아래에 작성자 */}
        {flat && <div className="text-[11px] text-slate-500 font-normal">{r.authorName}{r.authorDept ? ` · ${r.authorDept}` : ''}</div>}
      </td>
      <td className="py-2 pr-2 whitespace-nowrap">{r.docType}</td>
      <td className="py-2 pr-2 whitespace-nowrap"
        title={`${r.attributedBasis} 기준 · 작성일 ${r.docDate}`}>
        <span className={r.attributedBasis === '작성일' ? 'text-slate-400' : 'text-slate-700 font-medium'}>
          {api.attributedMonthLabel(r.attributedFrom, r.attributedTo)}
        </span>
      </td>
      <td className="py-2 pr-2 text-xs text-slate-500">
        {/* 구입지급결의서는 계정과목을 안 받으므로 지출 분류를 보여 준다.
            ⭐분류가 빈 문서는 (미지정)으로 **눈에 띄게** — 안 보이면 잘못 분류된
              문서를 찾을 방법이 없다(판매물품 오분류는 매입을 자동으로 만든다). */}
        {r.docType === '구입지급결의서' ? (
          <span className={`px-1.5 py-0.5 rounded font-semibold ${SPEND_BADGE[r.spendCategory ?? '(미지정)'] ?? 'bg-slate-100 text-slate-600'}`}>
            {r.spendCategory ?? '(미지정)'}
          </span>
        ) : r.accounts.length > 0 ? r.accounts.join('·') : '-'}
      </td>
      <td className="py-2 pr-2 text-right num font-medium whitespace-nowrap">{fmtKrw(r.totalAmount)}</td>
      <RemitCell row={r} canMark={canMarkRemit}
        pick={flat ? { on: picked.has(r.id), toggle: () => togglePick(r.id) } : null}
        onMark={() => openRemit(r)} />
      {/* 내용 칸 상한 — 1280 에선 190px(v2.268.0 실측: 260 이면 긴 목적 글이 있을 때 표가 62px 넘쳤다 · v2.267.0 이 「송금 · 지급일」 칸을 더하며 생긴 것) */}
    <td className="py-2 pr-2 text-xs text-slate-500 max-w-[190px] min-[1360px]:max-w-[260px] truncate">
        {r.purpose ?? r.vendor ?? (r.cardNo ? `카드 ${r.cardNo}` : '-')}
      </td>
      <td className="py-2 !px-1 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        {r.attachmentId && (
          <button type="button" title="확정 PDF 미리보기"
            className="text-slate-400 hover:text-blue-600"
            onClick={() => openServerPdfPreview({
              pdfUrl: api.attachmentDownloadUrl(r.attachmentId!),
              filename: `${r.docNo}_${r.docType}.pdf`,
            })}>
            📄
          </button>
        )}
        {/* 건별 엑셀 (v2.111.0) — 법인카드사용내역서만. 결재판·서명 없이 작성 내용만 */}
        {r.docType === '법인카드사용내역서' && (
          <button type="button" title="이 건의 사용내역을 엑셀로 내려받기 (서명 없이 내용만)"
            className="ml-1 text-slate-400 hover:text-emerald-600"
            onClick={async () => {
              try { await api.downloadExpenseDocXlsx(r.id, r.docNo); }
              catch (e) { window.alert((e as Error).message); }
            }}>
            📊
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      {/* 작성 + 내 작성중 문서 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <div>
            <h2 className="ui-section-title">지출결재 문서 작성</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              작성자 본인과 결재선(부서장·경영지원·대표이사)만 볼 수 있습니다. 제출하면{' '}
              <Link to="/approvals" className="text-blue-600 underline">내 결재함</Link> 흐름으로 승인이 진행됩니다.
              {/* v2.134.0 — 카드 문서를 이 목록에서 뺐다. 「작성은 저기서」만 적어 두면
                  「완결된 카드 문서가 없어졌다」는 문의가 그대로 온다. 열람까지 적는다. */}
              법인카드사용내역서는 <Link to="/expenses?tab=card" className="text-blue-600 underline">[법인카드 사용내역]</Link> 탭에서
              작성하고 <b>보관·열람</b>합니다 — 이 목록에는 나오지 않습니다.
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button"
              className="btn btn-primary"
              onClick={() => setOpenDoc({ id: null, type: '구입지급결의서' })}>
              + 구입지급결의서
            </button>
            <button type="button"
              className="btn btn-primary"
              onClick={() => setOpenDoc({ id: null, type: '개인경비정산서' })}>
              + 개인경비정산서
            </button>
          </div>
        </div>

        {myDrafts.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-semibold text-slate-500 mb-1.5">내 작성중·반려 문서 — 클릭해서 이어서 작성</div>
            <div className="flex flex-wrap gap-2">
              {myDrafts.map((d) => (
                <button key={d.id} type="button"
                  onClick={() => setOpenDoc({ id: d.id })}
                  className="btn btn-secondary btn-sm">
                  <span className={`px-1.5 py-0.5 rounded-full border text-[11px] font-semibold ${EXPENSE_STATUS_BADGE[d.status] ?? ''}`}>
                    {d.status}
                  </span>
                  <span className="font-medium text-slate-700">{d.docNo}</span>
                  <span className="text-slate-400">{d.docType}</span>
                  <span className="num text-slate-500">{fmtKrw(d.totalAmount)}원</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 완결 문서 목록 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h2 className="ui-section-title">완결(최종승인) 문서</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {flatPending ? (
                <>⏳ <b>송금 대기</b> — 지급일이 이른 순(지난 것은 빨강) · 문서 행 클릭 = 상세.
                  {canMarkRemit && ' 체크해서 고른 뒤 [💸 송금완료]로 한 번에 표시합니다.'}</>
              ) : (
                <>결재가 끝난 문서 보관 — <b>작성자 줄을 클릭하면</b> 그 사람의 문서가 펼쳐집니다.
                  문서 행 클릭 = 상세, 📄 = 확정본 PDF 미리보기.</>
              )}
              {isApprover && ' (결재선 당사자는 관할 문서 전체가 보입니다)'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select className={selCls} value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as api.ExpenseDocType | '')}>
              <option value="">전체 종류</option>
              {TAB_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className={selCls} value={year} onChange={(e) => setYear(Number(e.target.value))}
              disabled={remitFilter === 'pending'}
              title={remitFilter === 'pending' ? '송금 대기는 연도와 상관없이 모두 보여 줍니다' : undefined}>
              {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
            </select>
            <select className={selCls} value={account} onChange={(e) => setAccount(e.target.value)}>
              <option value="">전체 계정과목</option>
              {api.EXPENSE_ACCOUNTS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            {isApprover && (
              <select className={selCls} value={author} onChange={(e) => setAuthor(e.target.value)}>
                <option value="">전체 사용자</option>
                {(users.data ?? []).map((u) => (
                  <option key={u.userId} value={u.userId}>{u.name}{u.department ? ` (${u.department})` : ''}</option>
                ))}
              </select>
            )}
            <input className={`${selCls} w-44`} placeholder="번호·구매처·내용 검색"
              value={q} onChange={(e) => setQ(e.target.value)} />
            {/* 지출대장 엑셀 (v2.101.0) — ⭐화면 필터와 무관하게 **이 연도 전체** (대장은 그 해 전부여야
                결산·세무 제출에 쓸 수 있다 — 종류·검색 필터를 절반만 반영하면 "숫자가 안 맞는다"가 된다).
                ⚠plain <a href> 는 오류(세션 만료 등) 때 JSON 페이지로 이탈한다 — fetch+blob 으로 받는다 */}
            <button type="button"
              onClick={async () => {
                try {
                  const res = await fetch(`/api/expense/docs/export.xlsx?year=${year}`, { credentials: 'include' });
                  if (!res.ok) {
                    let msg = `내려받기 실패 (${res.status})`;
                    try { msg = (await res.json())?.error?.message ?? msg; } catch { /* JSON 아님 */ }
                    window.alert(msg);
                    return;
                  }
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `지출대장_${year}.xlsx`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  window.alert('내려받기에 실패했습니다 — 네트워크를 확인해 주세요');
                }
              }}
              className="btn btn-success"
              title="이 연도의 완결(최종승인) 지출 문서 전체를 엑셀 대장으로 내려받습니다 — 화면 필터와 무관하게 그 해 전부이고, 위 목록에서 뺀 **법인카드사용내역서도 함께** 담깁니다(결산·세무 제출용). 문서 목록 + 계정과목별 항목 상세 2장.">
              📥 대장 엑셀 (카드 포함)
            </button>
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <span>
              작성자 <b className="num">{groups.length}</b>명 · 문서 <b className="num">{totals.count}</b>건 · 합계{' '}
              <b className="num text-slate-800">{fmtKrw(totals.amount)}</b>원
            </span>
            {/* 💸 송금 거르기 (v2.267.0 · 개발 계획 #19) — 위 필터 줄에 칸을 더하면 1280 에서 두 줄로 접혀(881→1018px) 여기 둔다.
                대기 숫자 = 지금 거르기(종류·사용자·계정과목·검색)를 따르고 연도만 무관(v2.268.0 — 숫자와 목록이 같은 기준). */}
            <SegmentedControl<'all' | 'pending' | 'done'>
              ariaLabel="송금 거르기"
              value={remitFilter || 'all'}
              onChange={(k) => chooseRemitFilter(k === 'all' ? '' : k)}
              options={[
                { key: 'all', label: '송금 전체' },
                {
                  key: 'pending', label: '⏳ 송금 대기', count: remitPendingCount, tone: 'warn',
                  title: '대표이사 결재까지 끝났는데 아직 송금완료 표시가 없는 문서 — 연도와 상관없이 셉니다(다른 거르기는 따릅니다). 고르면 지급일 순으로 보여 줍니다',
                },
                { key: 'done', label: '✅ 송금완료' },
              ]}
            />
            {remitNotice && (
              <span className={remitNotice.startsWith('⚠') ? 'text-rose-700' : 'text-emerald-700'}>{remitNotice}</span>
            )}
          </div>
          {groups.length > 0 && !flatPending && (
            <div className="flex gap-1.5">
              <button type="button"
                onClick={() => setOpenAuthors(new Set(groups.map((g) => g.authorId)))}
                className="btn btn-secondary btn-sm">
                전체 펼치기
              </button>
              <button type="button" onClick={() => setOpenAuthors(new Set())}
                className="btn btn-secondary btn-sm">
                전체 접기
              </button>
            </div>
          )}
        </div>

        {/* 💸 여러 건 한 번에 송금완료 (v2.268.0 · 재검토 C) — 송금 대기 목록 · 경영지원·admin 에게만 */}
        {flatPending && canMarkRemit && pendingRows.length > 0 && (
          <div className="mb-2 flex items-center gap-x-3 gap-y-1.5 flex-wrap rounded border border-amber-200 bg-amber-50/70 px-3 py-1.5 text-xs text-slate-700">
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox" className="w-3.5 h-3.5"
                checked={pickedIds.length === pendingRows.length}
                onChange={(e) => setPicked(e.target.checked ? new Set(pendingRows.map((r) => r.id)) : new Set())} />
              전체 선택
            </label>
            <span>고른 문서 <b className="num">{pickedIds.length}</b>건</span>
            <span className="inline-flex items-center gap-1.5">
              송금일
              <DateInput value={bulkDate} onChange={setBulkDate} max={todayKst()} inputClassName="ui-input w-32" />
            </span>
            <button type="button" className="btn btn-primary btn-sm"
              disabled={pickedIds.length === 0 || bulkBusy} onClick={() => void runBulk()}>
              {bulkBusy ? '표시하는 중…' : `💸 고른 ${pickedIds.length}건 송금완료`}
            </button>
            <span className="text-slate-500">한 건씩 고치거나 풀 때는 「송금 전체」에서 체크칸을 누르세요.</span>
          </div>
        )}

        {/* 목록이 상한에서 잘리면 위 합계는 잘린 것만 더한 값이다 — 숨기지 않고 알린다 */}
        {truncated && (
          <div className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
            문서가 많아 <b>최근 500건까지만</b> 불러왔습니다 — 위 합계도, <b>📊 이 목록 요약도</b>, <b>작성자별 건수·합계도</b>
            그 500건 기준입니다. 연도·종류·작성자 필터로 좁혀 주세요.
          </div>
        )}

        {/* 📊 이 목록 요약 (v2.134.0) — 표 바로 위. 지금 화면 필터 기준이라는 것을 못박는다 */}
        {rows.length > 0 && (
          <div className="mb-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-bold text-slate-800">📊 이 목록 요약</span>
              {summary.byType.filter((t) => t.count > 0).map((t) => (
                <span key={t.type}>
                  {t.type} <b className="num">{t.count}</b>건
                  <span className="text-slate-500"> ({fmtKrw(t.amount)}원)</span>
                </span>
              ))}
              <span className="ml-auto text-xs text-slate-500">
                완결(최종승인)만 · 귀속월 기준 · 지금 화면 필터 기준
              </span>
            </div>
            {summary.categories.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-200 pt-1.5">
                <span className="text-slate-500">구입지급결의서 <b className="num text-slate-700">{summary.purchaseCount}</b>건 지출 분류 —</span>
                {summary.categories.map((c) => (
                  <span key={c.key}>
                    <span className={`px-1.5 py-0.5 rounded font-semibold ${SPEND_BADGE[c.key] ?? 'bg-slate-100 text-slate-600'}`}>{c.key}</span>
                    {' '}<b className="num">{fmtKrw(c.amount)}</b>원
                    <span className="text-slate-500"> ({c.count}건)</span>
                  </span>
                ))}
                <span className="text-xs text-slate-500">
                  개인경비·법인카드의 계정과목별 금액은 <Link to="/expenses?tab=stats" className="text-blue-600 hover:underline">[지출 현황]</Link> 탭에서
                </span>
              </div>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="data-table w-full text-[13px]">
            <thead>
              <tr className="text-left text-xs text-slate-500">
                <th className="py-2 pr-2 min-w-[150px]">문서번호</th>
                <th className="py-2 pr-2 min-w-[110px]">종류</th>
                <th className="py-2 pr-2 min-w-[110px]">몇 월분</th>
                {/* 구입지급결의서는 계정과목을 안 받는다(운영 실측 100% 빈칸) — 그 자리에 지출 분류를
                    함께 보여 준다. 칸을 새로 만들지 않아 폭이 늘지 않는다. */}
                <th className="py-2 pr-2 min-w-[120px] whitespace-nowrap">계정과목 / 지출 분류</th>
                <th className="py-2 pr-2 text-right min-w-[90px]">금액</th>
                {/* 💸 지급일(작성자) + 송금(경영지원) 한 칸 (v2.267.0) — 칸 둘을 늘리면 1280 에서 표가 넘친다 */}
                <th className="py-2 pr-2 min-w-[104px]">송금 · 지급일</th>
                <th className="py-2 pr-2 min-w-[160px]">내용</th>
                {/* 📄 확정 PDF + 📊 건별 엑셀(카드) 두 개가 들어가는 칸 (v2.111.0).
                    ⭐1280 실측 — 좌우 여백을 12px→4px 로 줄여 50px 에 담았다. 그냥 넓혔으면
                    표가 31px 더 넘쳤다(아이콘 1개이던 예전도 12px 넘치던 표라 더 밀면 안 된다). */}
                <th className="py-2 !px-1 w-[50px]"></th>
              </tr>
            </thead>
            <tbody>
              {finalQuery.isLoading ? (
                <tr><td colSpan={8} className="py-8 text-center text-slate-400"><LoadingLines /></td></tr>
              ) : groups.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-slate-400">
                  {remitFilter === 'pending' ? '송금 대기 문서가 없습니다'
                    : remitFilter === 'done' ? '송금완료로 표시된 문서가 없습니다' : '완결된 문서가 없습니다'}
                </td></tr>
              ) : flatPending ? (
                // 💸 송금 대기 — 지급일 순 한 줄 목록(작성자 묶음 없이 · 작성자는 문서번호 아래) (v2.268.0)
                pendingRows.map((r) => docRow(r, true))
              ) : groups.map((g) => {
                const open = openAuthors.has(g.authorId);
                return (
                  <Fragment key={g.authorId}>
                    {/* 작성자 줄 — 접었을 때 보이는 것: 이름 · 부서 · 최근 한 건 · 총계 */}
                    <tr className="border-t border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer"
                      onClick={() => toggleAuthor(g.authorId)}>
                      <td colSpan={8} className="!px-4 !py-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-slate-400 w-3 shrink-0">{open ? '▼' : '▶'}</span>
                          <span className="font-bold text-slate-800">{g.name}</span>
                          <span className="text-xs text-slate-500">{g.dept ?? '-'}</span>
                          <span className="text-xs text-slate-500">
                            최근{' '}
                            <b className="text-slate-600 font-medium">
                              {api.attributedMonthLabel(g.latest.attributedFrom, g.latest.attributedTo)}
                            </b>
                            <span className="ml-1.5 text-slate-400">{g.latest.docNo}</span>
                            <span className="ml-1.5 num text-slate-600">{fmtKrw(g.latest.totalAmount)}원</span>
                            {g.latest.backfillNote && <BackfillTag note={g.latest.backfillNote} />}
                          </span>
                          {g.remitPending > 0 && (
                            <span className="px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold">
                              ⏳ 송금 대기 {g.remitPending}
                            </span>
                          )}
                          <span className="ml-auto text-xs text-slate-500">
                            모두 <b className="num text-slate-700">{g.count}</b>건 ·{' '}
                            <b className="num text-slate-800">{fmtKrw(g.amount)}</b>원
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* 펼쳤을 때 — 그 사람의 문서들 (칼럼은 머리글과 그대로 맞는다) */}
                    {open && g.docs.map((r) => docRow(r, false))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openDoc && (
        <ExpenseDocModal
          docId={openDoc.id}
          docType={openDoc.type}
          onClose={() => setOpenDoc(null)}
        />
      )}
      {remitTarget && (
        <ExpenseRemitModal target={remitTarget} onClose={() => setRemitTarget(null)} onDone={setRemitNotice} />
      )}
    </div>
  );
}

/**
 * 💸 송금 · 지급일 칸 (v2.267.0 · 개발 계획 #19) — 윗줄 송금 상태(경영지원은 체크칸) · 아랫줄 작성자 지급일.
 * ⭐상태는 서버가 정한 remitState 로만 그린다(v2.268.0 · 상세 창과 같은 RemitStateLabel).
 * 체크칸:
 *   · 보통 — 누르면 송금일 창(기본 오늘 · 이미 완료면 날짜 고치기·풀기)
 *   · 송금 대기 한 줄 목록(pick) — 누르면 「고르기」만 · 위의 [💸 고른 N건 송금완료]로 한 번에 (v2.268.0)
 * 줄 클릭(상세 열기)과 섞이지 않게 막는다. 지급일이 지난 대기 건은 지급일 글도 빨갛게.
 */
function RemitCell({ row: r, canMark, onMark, pick }: {
  row: api.ExpenseDocListRow;
  canMark: boolean;
  onMark: () => void;
  pick: { on: boolean; toggle: () => void } | null;
}) {
  const state = r.remitState;
  const markable = canMark && (state === 'pending' || state === 'done' || state === 'legacy');
  const due = state === 'pending' ? payDueOf(r.payDate) : null;
  const overdue = due === 'overdue';
  // 칸 안에서는 올해 날짜를 「10-13」으로 줄여 쓴다(보이는 모양만 · 온전한 글은 title) — 전체 날짜면 칸이 133px 로 커져
  //   옆 「계정과목 / 지출 분류」 머리글이 두 줄로 접혔다(1280 실측 · v2.268.0)
  const payShort = (r.payDateShown ?? '').replace(new RegExp(`^${todayKst().slice(0, 4)}-`), '');
  return (
    <td className="py-2 pr-2 text-xs whitespace-nowrap" onClick={markable ? (e) => e.stopPropagation() : undefined}>
      <span className="inline-flex items-center gap-1">
        {markable && (pick ? (
          <input type="checkbox" className="w-3.5 h-3.5 cursor-pointer" checked={pick.on}
            title="고르기 — 위의 [💸 고른 N건 송금완료]로 한 번에 표시합니다"
            aria-label={`${r.docNo} 고르기`}
            onChange={pick.toggle} />
        ) : (
          <input type="checkbox" className="w-3.5 h-3.5 cursor-pointer" checked={state === 'done'}
            title={state === 'done' ? '송금일 고치기·송금완료 풀기' : '송금완료로 표시 — 송금일(기본 오늘)을 확인합니다'}
            aria-label={`${r.docNo} 송금완료`}
            onChange={onMark} />
        ))}
        <RemitStateLabel state={state} remitDate={r.remitDate} remitByName={r.remitByName} payDate={r.payDate} compact />
      </span>
      {r.payDateShown && (
        <div className={`text-[11px] max-w-[130px] truncate ${overdue ? 'text-rose-600 font-semibold' : due === 'today' ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}
          title={`지급일 ${r.payDateShown}${overdue ? ' — 지났습니다' : due === 'today' ? ' — 오늘입니다' : ''}`}>
          지급 {payShort}{overdue ? ' 지남' : due === 'today' ? ' 오늘' : ''}
        </div>
      )}
    </td>
  );
}

/** 「소급」 배지 — 포털 밖에서 결재가 끝나고 나중에 넣은 문서 (v2.57.0) */
function BackfillTag({ note }: { note: string }) {
  return (
    <span title={note}
      className="ml-1 px-1 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-semibold align-middle">
      소급
    </span>
  );
}
